/**
 * Jyotish Tarang — Birth Time Rectification Page v2.0
 * Expert only — accessible from chart card on Dashboard
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BuyCreditModal from '../components/BuyCreditModal';

function getToken() {
  return sessionStorage.getItem('access_token') || localStorage.getItem('access_token') || '';
}
async function apiFetch(url: string, options?: any) {
  const token = getToken();
  const h: any = { 'Content-Type': 'application/json' };
  if (token) h['Authorization'] = `Bearer ${token}`;
  return fetch(url, { ...options, headers: { ...h, ...(options?.headers || {}) } });
}

export default function RectificationPage() {
  const { id }   = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Phase 5 (14 Jul 2026, one-time-payments): Rectification used to be a
  // hard Platinum-only wall (isExpert gate below, now removed) blocking
  // every other tier from reaching this page at all. Backend already
  // gates both analyse() and apply() independently via gate_feature()
  // (Phase 3, v4.2.2) -- Platinum gets a free monthly allowance, everyone
  // else can pay ₹99/action via BuyCreditModal. See rectify.py lines
  // ~99 and ~201 for the two gate_feature() calls.

  const EVENT_TYPES = [
    { val:'marriage',      label:t('rectification.eventTypes.marriage') },
    { val:'divorce',       label:t('rectification.eventTypes.divorce') },
    { val:'job_new',       label:t('rectification.eventTypes.jobNew') },
    { val:'job_loss',      label:t('rectification.eventTypes.jobLoss') },
    { val:'child',         label:t('rectification.eventTypes.child') },
    { val:'property',      label:t('rectification.eventTypes.property') },
    { val:'travel',        label:t('rectification.eventTypes.travel') },
    { val:'illness',       label:t('rectification.eventTypes.illness') },
    { val:'financial_gain',label:t('rectification.eventTypes.financialGain') },
    { val:'financial_loss',label:t('rectification.eventTypes.financialLoss') },
    { val:'death_family',  label:t('rectification.eventTypes.deathFamily') },
    { val:'education',     label:t('rectification.eventTypes.education') },
    { val:'legal',         label:t('rectification.eventTypes.legal') },
    { val:'accident',      label:t('rectification.eventTypes.accident') },
    { val:'other',         label:t('rectification.eventTypes.other') },
  ];

  const [chart,      setChart]      = useState<any>(null);
  const [events,     setEvents]     = useState<any[]>([]);
  const [step,       setStep]       = useState(1);
  const [loading,    setLoading]    = useState(true);
  const [analysing,  setAnalysing]  = useState(false);
  const [applying,   setApplying]   = useState(false);
  const [result,     setResult]     = useState<any>(null);
  const [error,      setError]      = useState('');
  const [window_m,   setWindowM]    = useState(30);
  const [applied,    setApplied]    = useState(false);
  const [showBuyRectify, setShowBuyRectify] = useState(false);
  // Tracks which gated action to re-run after a successful ₹99 purchase --
  // analyse() and apply() are gated independently on the backend.
  const [pendingAction,  setPendingAction]  = useState<'analyse'|'apply'|null>(null);

  // New event form
  const [newCat,   setNewCat]   = useState('marriage');
  const [newDate,  setNewDate]  = useState('');
  const [newOutcome,setNewOutcome]=useState('positive');
  const [newNote,  setNewNote]  = useState('');
  const [saving,   setSaving]   = useState(false);

  useEffect(() => {
    Promise.all([
      apiFetch(`/api/charts/${id}`).then((r: any) => r.json()),
      apiFetch(`/api/charts/${id}/life-events`).then((r: any) => r.json()),
    ]).then(([c, ev]: any[]) => {
      setChart(c);
      // Parse outcome from description field
      setEvents((Array.isArray(ev) ? ev : []).map((e: any) => {
        const desc = e.description || '';
        const outcome = desc.includes('|') ? desc.split('|')[0] : 'positive';
        const note    = desc.includes('|') ? desc.split('|')[1] : desc;
        return { ...e, outcome, note };
      }));
      setLoading(false);
    }).catch(() => { setError(t('rectification.errors.loadFailed')); setLoading(false); });
  }, [id]);

  async function handleAddEvent() {
    if (!newDate) { setError(t('rectification.errors.selectDate')); return; }
    setSaving(true);
    try {
      await apiFetch(`/api/charts/${id}/life-events`, {
        method: 'POST',
        body: JSON.stringify({
          // 15 Aug 2026 fix: backend (rectify.py::add_life_event) already
          // prefixes description with the separately-sent `outcome` field
          // itself (stores f"{outcome}|{description}"). Sending an
          // already-prefixed description here on top of that double-encoded
          // it to "outcome|outcome|note" -- so the note re-parsed on load
          // (desc.split('|')[1]) showed the outcome string again instead of
          // the user's actual note. Send the raw note; let the backend do
          // the one-time prefixing it already does.
          category: newCat, event_date: newDate,
          outcome: newOutcome, description: newNote,
        }),
      });
      const ev: any = await apiFetch(`/api/charts/${id}/life-events`).then((r: any) => r.json());
      setEvents((Array.isArray(ev) ? ev : []).map((e: any) => {
        const desc = e.description || '';
        const outcome = desc.includes('|') ? desc.split('|')[0] : 'positive';
        const note    = desc.includes('|') ? desc.split('|')[1] : desc;
        return { ...e, outcome, note };
      }));
      setNewCat('marriage'); setNewDate(''); setNewOutcome('positive'); setNewNote('');
      setError('');
    } catch { setError(t('rectification.errors.saveFailed')); }
    finally { setSaving(false); }
  }

  async function handleDeleteEvent(eid: number) {
    await apiFetch(`/api/charts/${id}/life-events/${eid}`, { method: 'DELETE' });
    setEvents(events.filter((e: any) => e.id !== eid));
  }

  async function handleAnalyse() {
    if (events.length === 0) { setError(t('rectification.errors.addOneEvent')); return; }
    setAnalysing(true); setError(''); setResult(null);
    try {
      const r: any = await apiFetch(`/api/charts/${id}/rectify/analyse`, {
        method: 'POST',
        body: JSON.stringify({ window_mins: window_m, step_mins: 2, tz_offset: 5.5 }),
      });
      const d: any = await r.json();
      if (!r.ok) {
        if (d.credit_type === 'rectification') {
          setPendingAction('analyse');
          setShowBuyRectify(true);
          return;
        }
        setError(d.error || t('rectification.errors.analysisFailed'));
        return;
      }
      setResult(d);
      setStep(3);
    } catch { setError(t('rectification.errors.analysisFailedRetry')); }
    finally { setAnalysing(false); }
  }

  async function handleApply() {
    if (!result) return;
    setApplying(true);
    try {
      const r: any = await apiFetch(`/api/charts/${id}/rectify/apply`, {
        method: 'POST',
        body: JSON.stringify({
          rectified_time: result.best_time,
          confidence: result.confidence,
          // Snapshot of the analysis so the verdict can be appended to the
          // Platinum PDF report (Phase 3, 10 Jul 2026) — the raw top3/notes
          // only exist in this analyse() response, not persisted anywhere.
          candidates: (result.top3 || []).map((c: any) => ({
            time: c.time, lagna_sign_name: c.lagna_sign_name, score: c.score,
          })),
          notes: result.notes || '',
        }),
      });
      const d: any = await r.json();
      if (!r.ok) {
        if (d.credit_type === 'rectification') {
          setPendingAction('apply');
          setShowBuyRectify(true);
          return;
        }
        setError(d.error || t('rectification.errors.applyFailed'));
        return;
      }
      setApplied(true);
    } catch { setError(t('rectification.errors.applyFailed')); }
    finally { setApplying(false); }
  }

  async function handleRectifyPurchased() {
    setShowBuyRectify(false);
    const action = pendingAction;
    setPendingAction(null);
    if (action === 'analyse') await handleAnalyse();
    else if (action === 'apply') await handleApply();
  }

  // Phase 5 (14 Jul 2026): hard Platinum-only wall removed -- every tier
  // can now reach this page. Platinum still gets a free monthly allowance
  // via gate_feature(); everyone else pays ₹99/action via BuyCreditModal,
  // wired into handleAnalyse()/handleApply() above.

  if (loading) return (
    <div className="page-wrap">
      <div className="container-sm" style={{ textAlign: 'center', paddingTop: 60 }}>
        {t('common.loading')}
      </div>
    </div>
  );

  const stepLabels = [t('rectification.steps.lifeEvents'), t('rectification.steps.searchWindow'), t('rectification.steps.results')];

  return (
    <div className="page-wrap">
      <div className="container-sm">

        {/* Header */}
        <div style={{ marginBottom: 20 }}>
          <button className="btn btn-ghost btn-sm"
                  onClick={() => navigate('/dashboard')}
                  style={{ marginBottom: 8 }}>
            ← {t('nav.dashboard')}
          </button>
          <h2 style={{ fontFamily: 'var(--font-display)', color: 'var(--indigo)', marginBottom: 4 }}>
            🕐 {t('nav.rectification')}
          </h2>
          <p style={{ fontSize: 13, color: 'var(--ink3)' }}>
            {chart?.name} — {chart?.dob} {t('muhurta.at')} {chart?.tob}
          </p>
        </div>

        {/* Intro box */}
        <div style={{
          background: 'var(--indigo)', color: '#fff',
          borderRadius: 10, padding: '14px 16px', marginBottom: 20,
        }}>
          <div style={{ fontSize: 13, lineHeight: 1.6, opacity: 0.9 }}>
            {t('rectification.introText')}
          </div>
        </div>

        {/* Step indicators */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
          {stepLabels.map((s, i) => (
            <div key={i} style={{
              flex: 1, textAlign: 'center', padding: '8px',
              background: step === i+1 ? 'var(--indigo)' : step > i+1 ? 'var(--teal)' : 'var(--ivory)',
              color: step >= i+1 ? '#fff' : 'var(--ink3)',
              borderRadius: 8, fontSize: 12, fontWeight: 600,
            }}>{i+1}. {s}</div>
          ))}
        </div>

        {error && <div className="info-box info-red" style={{ marginBottom: 16 }}>{error}</div>}

        {/* Step 1 — Life Events */}
        {step === 1 && (
          <div>
            {/* Existing events */}
            {events.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <h3 style={{ fontSize: 13, color: 'var(--indigo)', marginBottom: 10 }}>
                  {t('rectification.savedLifeEvents')}
                </h3>
                {events.map((e: any) => (
                  <div key={e.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '10px 12px', background: 'var(--ivory)', borderRadius: 8,
                    marginBottom: 6, border: '0.5px solid var(--parch2)',
                  }}>
                    <div>
                      <span style={{ fontWeight: 600, color: 'var(--indigo)', fontSize: 13 }}>
                        {EVENT_TYPES.find(et => et.val === e.category)?.label || e.category}
                      </span>
                      <span style={{ fontSize: 12, color: 'var(--ink3)', marginLeft: 8 }}>
                        {e.event_date} · {e.outcome}
                      </span>
                      {e.note && (
                        <span style={{ fontSize: 11, color: 'var(--ink3)', marginLeft: 6 }}>
                          — {e.note}
                        </span>
                      )}
                    </div>
                    <button onClick={() => handleDeleteEvent(e.id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer',
                                     color: 'var(--red)', fontSize: 16 }}>✕</button>
                  </div>
                ))}
              </div>
            )}

            {/* Add new event form */}
            <div className="card">
              <h3 style={{ fontSize: 13, color: 'var(--indigo)', marginBottom: 14 }}>
                {events.length === 0 ? t('rectification.addFirstEvent') : t('rectification.addAnotherEvent')}
              </h3>
              <div className="form-group">
                <label className="form-label">{t('rectification.eventType')} *</label>
                <select className="form-input" value={newCat}
                        onChange={e => setNewCat(e.target.value)}>
                  {EVENT_TYPES.map(et => (
                    <option key={et.val} value={et.val}>{et.label}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">{t('rectification.dateOfEvent')} *</label>
                  <input className="form-input" type="date" value={newDate}
                         onChange={e => setNewDate(e.target.value)} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">{t('rectification.outcome')}</label>
                  <select className="form-input" value={newOutcome}
                          onChange={e => setNewOutcome(e.target.value)}>
                    <option value="positive">✓ {t('rectification.outcomePositive')}</option>
                    <option value="negative">✗ {t('rectification.outcomeNegative')}</option>
                    <option value="neutral">~ {t('rectification.outcomeNeutral')}</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{t('rectification.notesOptional')}</label>
                <input className="form-input" type="text" value={newNote}
                       placeholder={t('rectification.notesPlaceholder')}
                       onChange={e => setNewNote(e.target.value)} />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-secondary" onClick={handleAddEvent} disabled={saving}>
                  {saving ? t('rectification.saving') : `+ ${t('rectification.addEvent')}`}
                </button>
                {events.length > 0 && (
                  <button className="btn btn-primary" onClick={() => setStep(2)}>
                    {t('muhurta.next')} →
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 2 — Search Window */}
        {step === 2 && (
          <div className="card">
            <h3 style={{ fontSize: 14, color: 'var(--indigo)', marginBottom: 16 }}>
              {t('rectification.howFarOff')}
            </h3>
            {[
              { val: 15, label: t('rectification.windowOptions.min15'), sub: t('rectification.windowOptions.min15Sub') },
              { val: 30, label: t('rectification.windowOptions.min30'), sub: t('rectification.windowOptions.min30Sub') },
              { val: 60, label: t('rectification.windowOptions.min60'), sub: t('rectification.windowOptions.min60Sub') },
            ].map(opt => (
              <div key={opt.val}
                   onClick={() => setWindowM(opt.val)}
                   style={{
                     padding: '14px 16px', borderRadius: 10, cursor: 'pointer',
                     marginBottom: 10,
                     background: window_m === opt.val ? 'var(--indigo)' : 'var(--ivory)',
                     border: `1.5px solid ${window_m === opt.val ? 'var(--indigo)' : 'var(--parch2)'}`,
                   }}>
                <div style={{
                  fontSize: 14, fontWeight: 600,
                  color: window_m === opt.val ? '#fff' : 'var(--indigo)',
                }}>
                  {window_m === opt.val ? '● ' : '○ '}{opt.label}
                </div>
                <div style={{
                  fontSize: 12,
                  color: window_m === opt.val ? 'rgba(255,255,255,0.75)' : 'var(--ink3)',
                  marginTop: 3,
                }}>
                  {opt.sub}
                </div>
              </div>
            ))}
            <div className="info-box info-teal" style={{ marginBottom: 16, fontSize: 12 }}>
              {t('rectification.windowNote', { count: window_m * 2 })}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost" onClick={() => setStep(1)}>← {t('common.back')}</button>
              <button className="btn btn-primary" onClick={handleAnalyse} disabled={analysing}>
                {analysing ? `🔍 ${t('rectification.analysing')}` : `🔍 ${t('rectification.findBestTime')} →`}
              </button>
            </div>
          </div>
        )}

        {/* Step 3 — Results */}
        {step === 3 && result && !applied && (
          <div>
            {/* Best match banner */}
            <div style={{
              background: 'var(--teal)', color: '#fff',
              borderRadius: 12, padding: '20px 24px', marginBottom: 16,
            }}>
              <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 8 }}>{t('rectification.bestMatchFound')}</div>
              <div style={{ fontSize: 32, fontWeight: 700, marginBottom: 4 }}>
                🎯 {result.best_time}
              </div>
              <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 12 }}>
                {t('rectification.confidence')}: {result.confidence}%
              </div>
              {/* Confidence bar */}
              <div style={{ height: 8, background: 'rgba(255,255,255,0.3)', borderRadius: 4 }}>
                <div style={{
                  height: 8, borderRadius: 4, background: '#F4C430',
                  width: `${result.confidence}%`,
                }}/>
              </div>
              <div style={{ fontSize: 11, opacity: 0.7, marginTop: 6 }}>
                {t('rectification.testedCandidates', { count: result.total_tested })}
              </div>
            </div>

            {/* Before / After comparison */}
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16,
            }}>
              <div style={{
                padding: '14px 16px', background: '#FEF2F2',
                borderRadius: 10, border: '1px solid var(--red)',
              }}>
                <div style={{ fontSize: 11, color: 'var(--red)', fontWeight: 600, marginBottom: 6 }}>
                  ⚠ {t('rectification.original')}
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--indigo)' }}>
                  {result.original_time}
                </div>
                <div style={{ fontSize: 12, color: 'var(--ink2)', marginTop: 4 }}>
                  {result.original_lagna_sign} {result.original_lagna_deg?.toFixed(1)}°
                </div>
              </div>
              <div style={{
                padding: '14px 16px', background: '#F0FDF4',
                borderRadius: 10, border: '1px solid var(--teal)',
              }}>
                <div style={{ fontSize: 11, color: 'var(--teal)', fontWeight: 600, marginBottom: 6 }}>
                  ★ {t('rectification.rectified')}
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--indigo)' }}>
                  {result.best_time}
                </div>
                <div style={{ fontSize: 12, color: 'var(--ink2)', marginTop: 4 }}>
                  {result.top3?.[0]?.lagna_sign_name} {result.top3?.[0]?.lagna_lon?.toFixed(1)}°
                </div>
              </div>
            </div>

            {/* Event alignment */}
            <div className="card" style={{ marginBottom: 16 }}>
              <h3 style={{ fontSize: 13, color: 'var(--indigo)', marginBottom: 12 }}>
                {t('rectification.howEventsAligned')}
              </h3>
              {result.top3?.[0]?.event_scores?.map((es: any, i: number) => (
                <div key={i} style={{
                  padding: '10px 12px', background: 'var(--ivory)',
                  borderRadius: 8, marginBottom: 8,
                  borderLeft: `3px solid ${es.score >= 60 ? 'var(--teal)' : es.score >= 30 ? 'var(--sfb)' : 'var(--red)'}`,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--indigo)' }}>
                      {EVENT_TYPES.find(et => et.val === es.event_type)?.label || es.event_type}
                    </span>
                    <span style={{
                      fontSize: 12, fontWeight: 700,
                      color: es.score >= 60 ? 'var(--teal)' : es.score >= 30 ? 'var(--sfb)' : 'var(--red)',
                    }}>
                      {es.score}/100
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--ink3)', marginBottom: 4 }}>
                    {es.event_date}
                    {es.dasha?.md && ` · MD: ${es.dasha.md} / AD: ${es.dasha.ad}`}
                  </div>
                  {es.breakdown?.map((b: string, j: number) => (
                    <div key={j} style={{ fontSize: 11, color: 'var(--teal)' }}>✓ {b}</div>
                  ))}
                </div>
              ))}
            </div>

            {/* Runner-ups */}
            {result.top3?.length > 1 && (
              <div className="card" style={{ marginBottom: 16 }}>
                <h3 style={{ fontSize: 13, color: 'var(--indigo)', marginBottom: 10 }}>
                  {t('rectification.runnerUpTimes')}
                </h3>
                {result.top3.slice(1).map((c: any, i: number) => (
                  <div key={i} style={{
                    display: 'flex', justifyContent: 'space-between',
                    padding: '8px 12px', background: 'var(--ivory)',
                    borderRadius: 8, marginBottom: 6, fontSize: 13,
                  }}>
                    <span style={{ color: 'var(--indigo)', fontWeight: 600 }}>
                      #{i+2} {c.time} — {c.lagna_sign_name} {c.lagna_lon?.toFixed(1)}°
                    </span>
                    <span style={{ color: 'var(--ink3)' }}>{t('rectification.score')}: {c.score?.toFixed(1)}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button className="btn btn-ghost" onClick={() => { setStep(1); setResult(null); }}>
                ← {t('rectification.tryAgain')}
              </button>
              <button className="btn btn-primary"
                      onClick={handleApply} disabled={applying}
                      style={{ background: 'var(--teal)', borderColor: 'var(--teal)' }}>
                {applying ? t('rectification.creatingChart') : `✓ ${t('rectification.applyAsRectified')}`}
              </button>
            </div>
            <p style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 8 }}>
              {t('rectification.applyNote')}
            </p>
          </div>
        )}

        {/* Applied success */}
        {applied && (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🎯</div>
            <h3 style={{ color: 'var(--teal)', marginBottom: 8 }}>
              {t('rectification.chartCreated')}
            </h3>
            <p style={{ color: 'var(--ink2)', fontSize: 13, marginBottom: 24, maxWidth: 400, margin: '0 auto 24px' }}>
              {t('rectification.chartCreatedNote', { time: result?.best_time })}
            </p>
            <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>
              {t('rectification.goToDashboard')}
            </button>
          </div>
        )}

      </div>
      <BuyCreditModal
        open={showBuyRectify}
        creditType="rectification"
        onClose={() => { setShowBuyRectify(false); setPendingAction(null); }}
        onPurchased={handleRectifyPurchased}
      />
    </div>
  );
}
